'use client'

/**
 * StandardHeroCard - Unified hero/summary card for all calculator tools
 * Provides consistent layout with flexible configuration for different tool needs
 */

import React from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  XCircle,
  Share2,
  LucideIcon
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Metric,
  StatusConfig,
  BannerConfig
} from './types'
import {
  statusGradients,
  statusBackgrounds,
  statusBorders,
  statusTextColors
} from '@/lib/design-tokens'

interface StandardHeroCardProps {
  // Primary metric (optional - shown prominently above grid)
  primaryMetric?: Metric & {
    size?: 'small' | 'medium' | 'large'
  }

  // Supporting metrics grid (2-4 items recommended)
  metrics: Metric[]

  // Status indicator (optional)
  status?: StatusConfig

  // Banner callout (optional - shown below metrics)
  banner?: BannerConfig

  // Share button configuration
  onShare?: () => void
  shareUrl?: string

  // Custom content slot (for visualizations like height rulers)
  children?: React.ReactNode

  // Layout customization
  metricsColumns?: 2 | 3 | 4
  className?: string
}

const statusIcons: Record<string, LucideIcon> = {
  optimal: CheckCircle,
  acceptable: AlertCircle,
  warning: AlertTriangle,
  critical: AlertTriangle,
  neutral: AlertCircle,
}

export function StandardHeroCard({
  primaryMetric,
  metrics,
  status,
  banner,
  onShare,
  shareUrl,
  children,
  metricsColumns = 4,
  className,
}: StandardHeroCardProps) {
  const StatusIcon = status ? statusIcons[status.level] : null

  // Determine grid columns based on metrics count and preference
  const actualColumns = Math.min(metrics.length, metricsColumns)
  const gridColsClass = {
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-3',
    4: 'grid-cols-2 md:grid-cols-4',
  }[actualColumns] || 'grid-cols-2 md:grid-cols-4'

  return (
    <Card className={cn('overflow-hidden', className)}>
      {/* Status Header Bar */}
      {status && (
        <div className={cn(
          'px-3 sm:px-6 py-2.5 sm:py-4 text-white bg-linear-to-r',
          statusGradients[status.level],
          status.showPulse && 'animate-pulse'
        )}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              {status.showIcon !== false && StatusIcon && (
                <StatusIcon className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" aria-hidden="true" />
              )}
              <p className="text-sm sm:text-lg font-semibold truncate">{status.message}</p>
            </div>
            {(onShare || shareUrl) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onShare}
                className="text-white hover:bg-white/20 min-h-[44px] px-2 sm:px-3 flex-shrink-0"
                aria-label="Share results"
              >
                <Share2 className="h-4 w-4 sm:mr-2" aria-hidden="true" />
                <span className="hidden sm:inline">Share</span>
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="p-3 sm:p-6 space-y-3 sm:space-y-6">
        {/* Primary Metric Display */}
        {primaryMetric && (
          <div className="text-center space-y-1 sm:space-y-2">
            <p className="text-xs sm:text-sm font-medium text-gray-600 uppercase tracking-wide">
              {primaryMetric.label}
            </p>
            <div className="flex items-center justify-center gap-2 sm:gap-3">
              {primaryMetric.icon && (
                <primaryMetric.icon
                  className={cn(
                    'text-green-600',
                    primaryMetric.size === 'large' && 'h-8 w-8 sm:h-12 sm:w-12',
                    primaryMetric.size === 'medium' && 'h-6 w-6 sm:h-8 sm:w-8',
                    primaryMetric.size === 'small' && 'h-5 w-5 sm:h-6 sm:w-6',
                    !primaryMetric.size && 'h-7 w-7 sm:h-10 sm:w-10'
                  )}
                  aria-hidden="true"
                />
              )}
              <div>
                <p className={cn(
                  'font-bold text-gray-900',
                  primaryMetric.size === 'large' && 'text-3xl sm:text-6xl',
                  primaryMetric.size === 'medium' && 'text-2xl sm:text-4xl',
                  primaryMetric.size === 'small' && 'text-xl sm:text-2xl',
                  !primaryMetric.size && 'text-2xl sm:text-5xl'
                )}>
                  {primaryMetric.value}
                  {primaryMetric.unit && (
                    <span className="text-lg sm:text-3xl text-gray-600 ml-1 sm:ml-2">
                      {primaryMetric.unit}
                    </span>
                  )}
                </p>
                {primaryMetric.subtext && (
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">
                    {primaryMetric.subtext}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Metrics Grid */}
        <div className={cn('grid gap-4', gridColsClass)}>
          {metrics.map((metric, index) => (
            <MetricCard key={index} metric={metric} />
          ))}
        </div>

        {/* Banner Callout */}
        {banner && (
          <div className={cn(
            'flex items-start gap-3 p-4 rounded-lg border',
            banner.variant === 'success' && 'bg-green-50 border-green-200',
            banner.variant === 'info' && 'bg-gray-50 border-gray-200',
            banner.variant === 'warning' && 'bg-amber-50 border-amber-200'
          )}>
            {banner.icon && (
              <banner.icon
                className={cn(
                  'h-5 w-5 mt-0.5 flex-shrink-0',
                  banner.variant === 'success' && 'text-green-600',
                  banner.variant === 'info' && 'text-gray-600',
                  banner.variant === 'warning' && 'text-amber-600'
                )}
                aria-hidden="true"
              />
            )}
            <p className={cn(
              'text-sm font-medium',
              banner.variant === 'success' && 'text-green-900',
              banner.variant === 'info' && 'text-gray-900',
              banner.variant === 'warning' && 'text-amber-900'
            )}>
              {banner.message}
            </p>
          </div>
        )}

        {/* Custom Content Slot */}
        {children && (
          <div className="pt-4 border-t">
            {children}
          </div>
        )}

        {/* Share Button (if no status header) */}
        {!status && (onShare || shareUrl) && (
          <div className="flex justify-end pt-4 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={onShare}
              className="min-h-[44px]"
              aria-label="Share results"
            >
              <Share2 className="h-4 w-4 mr-2" aria-hidden="true" />
              Share Results
            </Button>
          </div>
        )}
      </div>
    </Card>
  )
}

/**
 * Individual metric card within the grid
 * Mobile-optimized with responsive padding and text sizes
 */
function MetricCard({ metric }: { metric: Metric }) {
  const content = (
    <div className={cn(
      'flex flex-col items-start gap-1 sm:gap-2 p-3 sm:p-4 rounded-lg',
      'bg-gray-50 border border-gray-200',
      'transition-colors',
      metric.href && 'hover:bg-gray-100 cursor-pointer'
    )}>
      <div className="flex items-center gap-1.5 sm:gap-2 w-full">
        {metric.icon && (
          <metric.icon
            className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-600 flex-shrink-0"
            aria-hidden="true"
          />
        )}
        <p className="text-[10px] sm:text-xs font-medium text-gray-600 uppercase tracking-wide leading-tight">
          {metric.label}
        </p>
      </div>
      <p className="text-lg sm:text-2xl font-bold text-gray-900 leading-tight">
        {metric.value}
      </p>
      {metric.subtext && (
        <p className="text-[11px] sm:text-xs text-gray-600 leading-tight">
          {metric.subtext}
        </p>
      )}
    </div>
  )

  if (metric.href) {
    return (
      <a href={metric.href} className="block focus:outline-hidden focus:ring-2 focus:ring-green-500 rounded-lg min-h-[44px]">
        {content}
      </a>
    )
  }

  return content
}
